import { useId, useState, type ReactNode } from "react";

import { DOMAINS, domainVar } from "../lib/widgetDomain.mjs";
import { noteControlLabel, type NoteSection } from "../lib/widgetNote.mjs";

/**
 * MER-604: the technical definition behind a number, on demand.
 * MER-558: and what to do about it when it moves.
 *
 * Sander asked for this on 2026-03-08: "metric definition notes on widgets
 * showing the data source columns behind each number". The manifests have
 * carried `widget_note` ever since and nothing rendered it.
 *
 * It is deliberately NOT a second copy of the tile caption. `what_it_shows`
 * renders under every tile and answers "what am I looking at" at a glance;
 * the panel repeats that one sentence as its opening section and then answers
 * the three questions that do not fit on the face of a card: where the number
 * comes from, what an owner does when it moves, and what good looks like. The
 * opening section is the SAME manifest field the caption reads, so the card
 * and the panel cannot drift apart (MER-558 ask 2).
 *
 * Shape: a 16px control in the tile chrome, disclosure below. The control is
 * a real <button> -- keyboard reachable, named, with aria-expanded -- because
 * a div with an onClick is exactly the thing a keyboard user cannot reach,
 * and the note is most valuable to the reader who cannot skim the whole page
 * at once. The panel is always in the DOM (hidden when collapsed) so
 * aria-controls always resolves.
 */
export interface WidgetNoteProps {
  /**
   * MER-558: the panel's sections, already through noteSections(). What this
   * measures, how it is measured, what to do when it moves, what good looks
   * like -- whichever of those the manifest carries for this widget.
   */
  sections: NoteSection[];
  /** The widget's title. Names the control, with the sections present. */
  label: string;
  /** Domain key for the open-state accent. Defaults to operations. */
  domain?: string;
  /** Offset of the control inside the nearest positioned ancestor. */
  top?: number;
  right?: number;
}

/**
 * Whether this focus came from the keyboard. The disclosure opens on keyboard
 * focus (so a tab-through reader gets the definition without a second
 * keystroke) but NOT on the focus a mouse click leaves behind, which would
 * fight the click-to-toggle. Browsers own the :focus-visible heuristic; a
 * browser without it treats every focus as keyboard focus, which errs toward
 * showing the note.
 */
function isKeyboardFocus(el: HTMLElement): boolean {
  try {
    return typeof el.matches !== "function" || el.matches(":focus-visible");
  } catch {
    return true;
  }
}

export default function WidgetNote(props: WidgetNoteProps) {
  const { sections, label, top = 10, right = 10 } = props;
  const domain = props.domain && DOMAINS.includes(props.domain) ? props.domain : "operations";
  const panelId = useId();
  const [pinned, setPinned] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const open = pinned || keyboardFocus;
  const accent = domainVar(domain);

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={noteControlLabel(label, sections)}
        onClick={(ev) => {
          // The tile itself is the drill-down target (WidgetRenderer wraps it
          // in a role="button" div). Asking what a number means must never
          // also open the rows behind it.
          ev.stopPropagation();
          setPinned((v) => !v);
        }}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " " || ev.key === "Escape") {
            ev.stopPropagation();
          }
          if (ev.key === "Escape") {
            // Close but keep focus where the reader put it.
            setPinned(false);
            setKeyboardFocus(false);
          }
        }}
        onFocus={(ev) => {
          if (isKeyboardFocus(ev.currentTarget)) setKeyboardFocus(true);
        }}
        onBlur={() => setKeyboardFocus(false)}
        style={{
          position: "absolute",
          top,
          right,
          zIndex: 2,
          width: 16,
          height: 16,
          padding: 0,
          borderRadius: 999,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          fontWeight: 700,
          border: `0.5px solid ${open ? accent : "var(--border)"}`,
          background: open ? `var(--domain-${domain}-wash)` : "var(--panel)",
          color: open ? accent : "var(--text-muted)",
          cursor: "pointer",
        }}
      >
        <span aria-hidden="true">i</span>
      </button>
      <div
        id={panelId}
        // Always in the DOM so aria-controls resolves whether or not the
        // panel is open; `hidden` keeps the collapsed copy out of the
        // accessibility tree and out of the reader's way.
        hidden={!open}
        // MER-604: the fade lives in brand.css so the reduced-motion override
        // sits next to it (the house pattern from MER-170). The class is
        // applied only while open: a keyframe on a display:none element never
        // runs, so it has to arrive with the disclosure.
        className={open ? "meridian-widget-note" : undefined}
        style={{
          marginTop: 10,
          paddingTop: 8,
          borderTop: "0.5px solid var(--border)",
          fontSize: 10.5,
          lineHeight: 1.45,
          color: "var(--text-2)",
        }}
      >
        {sections.map((section, i) => (
          <div key={section.key} style={{ marginTop: i === 0 ? 0 : 8 }}>
            <div
              style={{
                fontSize: 8.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--text-muted)",
                marginBottom: 3,
              }}
            >
              {section.heading}
            </div>
            {section.body}
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * MER-604: the same control for a widget that has no tile chrome of its own.
 *
 * KPI cards have a header to hang the control in; charts, tables and pivots
 * render bare inside their grid cell. This reserves a 20px strip above the
 * widget for the control and opens the panel underneath it, so the note
 * cannot land on top of a chart legend or a table header. The frame sits
 * OUTSIDE the drill-down wrapper, so the control is never a nested
 * interactive element inside the tile's role="button".
 */
export function WidgetNoteFrame(props: {
  sections: NoteSection[];
  label: string;
  domain?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ position: "relative", paddingTop: 20 }}>
      {props.children}
      <WidgetNote
        sections={props.sections}
        label={props.label}
        domain={props.domain}
        top={0}
        right={0}
      />
    </div>
  );
}
